import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../api/apiClient';

export interface Task {
  _id: string;
  taskId?: string;
  title: string;
  description?: string;
  status: 'To Do' | 'In Progress' | 'Under Review' | 'Completed' | 'Rejected' | 'Closed';
  priority: 'Low' | 'Medium' | 'High' | 'Critical' | 'Urgent'; // Merged previous Urgent just in case
  type: 'Task' | 'Bug' | 'Story' | 'Improvement' | 'Documentation' | 'Research' | 'Meeting' | 'General' | 'Feature';
  category?: string;
  workItemType?: string;
  assignee?: { _id: string; username: string; email: string };
  reporter: { _id: string; username: string; email: string };
  project?: { _id: string; name: string };
  dueDate?: string;
  estimatedHours?: number;
  loggedHours?: number;
  attachments?: { url: string; filename: string; uploadedBy?: string; uploadedAt?: string }[];
  comments?: {
    _id: string;
    text: string;
    author: { _id: string; username: string; email: string };
    createdAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

interface TaskState {
  tasks: Task[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskState = {
  tasks: [],
  loading: false,
  error: null,
};

export const fetchTasks = createAsyncThunk('tasks/fetchTasks', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get('/tasks');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch tasks');
  }
});

export const createTask = createAsyncThunk('tasks/createTask', async (taskData: Partial<Omit<Task, 'assignee'>> & { assignee?: string }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post('/tasks', taskData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create task');
  }
});

export const updateTask = createAsyncThunk('tasks/updateTask', async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
  try {
    const response = await apiClient.put(`/tasks/${id}`, data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update task');
  }
});

export const updateTaskStatus = createAsyncThunk('tasks/updateTaskStatus', async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(`/tasks/${id}/status`, { status });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update task status');
  }
});

export const addComment = createAsyncThunk('tasks/addComment', async ({ id, text }: { id: string; text: string }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(`/tasks/${id}/comments`, { text });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to add comment');
  }
});

export const submitTask = createAsyncThunk('tasks/submitTask', async ({ id, notes, attachments }: { id: string; notes: string; attachments?: any[] }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(`/tasks/${id}/submit`, { notes, attachments });
    // Returns { task, submission }
    return response.data.task;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to submit task');
  }
});

export const reviewTask = createAsyncThunk('tasks/reviewTask', async ({ id, status, reviewerComments }: { id: string; status: string; reviewerComments?: string }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(`/tasks/${id}/review`, { status, reviewerComments });
    // Returns { task, submission }
    return response.data.task;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to review task');
  }
});

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    optimisticUpdateStatus: (state, action) => {
      const { id, status } = action.payload;
      const task = state.tasks.find((t) => t._id === id);
      if (task) {
        task.status = status;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
      })
      // Update full task
      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (index !== -1) state.tasks[index] = action.payload;
      })
      // Status Updates
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (index !== -1) state.tasks[index] = action.payload;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (index !== -1) state.tasks[index] = action.payload;
      })
      .addCase(submitTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (index !== -1) state.tasks[index] = action.payload;
      })
      .addCase(reviewTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (index !== -1) state.tasks[index] = action.payload;
      });
  },
});

export const { optimisticUpdateStatus } = taskSlice.actions;
export default taskSlice.reducer;
